/* eslint-disable react-hooks/exhaustive-deps */
import { Button, Dropdown, Breadcrumb, Spin, Avatar } from "antd";
import type { MenuProps } from "antd";
import {
  UserAddOutlined,
  CheckOutlined,
  CheckCircleOutlined,
  UserDeleteOutlined,
  LogoutOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { useState, useCallback, useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";
import type { Conversation, ChannelType } from "@interfaces/chat.interface";
import { ThemeToggle } from "@components/ui/ThemeToggle";
import { useChatStore } from "@stores/chatStore";
import { PLATFORM_GROUPS } from "@constants/platforms";
import { useAppSelector, useAppDispatch } from "@stores/redux/store";
import { useAuth } from "@adapters/hooks";
import { apiClient } from "@api/apiClient";
import {
  fetchAgentsByConversations,
  removeAgentFromConversation,
} from "@features/agents/agentsSlice";
import { PLATFORMS, CHANNELS } from "@common";
import {
  assignAgentToConversation,
  unassignAgentFromConversation,
} from "@api/services/agents";
import { markConversationAsDone } from "@api/services/facebook";
import { useNotificationContext } from "@hooks/useNotificationContext";
import FacebookIcon from "@assets/facebook-icon.svg";
import ZaloIcon from "@assets/zalo-icon.svg";
import ShopeeIcon from "@assets/shopee-icon.svg";
import { getAvatarGradient, getAvatarLetter } from "@utils/avatarUtils";
import { getLocalStorageJSON } from "@common";
import styles from "./styles.module.less";

interface FanPageHeaderProps {
  conversation?: Conversation;
  isOnline?: boolean;
  typingUsers?: string[];
  pageId?: string | null;
  isShowInfoUser?: boolean;
}

const getPlatformInfo = (channelType: ChannelType) => {
  switch (channelType) {
    case "facebook":
      return {
        label: "Facebook",
        icon: (
          <img
            src={FacebookIcon}
            alt="Facebook"
            className={styles.platformIcon}
          />
        ),
      };
    case "zalo":
      return {
        label: "Zalo",
        icon: <img src={ZaloIcon} alt="Zalo" className={styles.platformIcon} />,
      };
    case "shopee":
      return {
        label: "Shopee",
        icon: (
          <img src={ShopeeIcon} alt="Shopee" className={styles.platformIcon} />
        ),
      };
    default:
      return {
        label: "Facebook",
        icon: (
          <img
            src={FacebookIcon}
            alt="Facebook"
            className={styles.platformIcon}
          />
        ),
      };
  }
};

export const FanPageHeader = ({
  conversation,
  pageId,
  isShowInfoUser,
}: FanPageHeaderProps) => {
  const location = useLocation();
  const { success, error: showError } = useNotificationContext();
  const dispatch = useAppDispatch();
  const auth = useAuth();
  const selectedChannel = useChatStore((state) => state.selectedChannel);
  const selectedFanpageName = useChatStore(
    (state) => state.selectedFanpageName
  );
  const {
    agents,
    loading: loadingAgents,
    agentsByConversation,
  } = useAppSelector((state) => state.agents);
  const { pages: facebookPages } = useAppSelector((state) => state.fanpage);

  const activePageId = useMemo(() => {
    if (pageId) return pageId;
    if (selectedFanpageName && facebookPages.length > 0) {
      const selectedPage = facebookPages.find(
        (page) => page.name === selectedFanpageName
      );
      return selectedPage?.id || null;
    }
    return null;
  }, [pageId, selectedFanpageName, facebookPages]);

  const conversationFolderStatusMap = useChatStore(
    (state) => state.conversationFolderStatusMap
  );
  const updateConversationFolderStatus = useChatStore(
    (state) => state.updateConversationFolderStatus
  );

  const folderStatus = useMemo(() => {
    if (!conversation?.lastMessage?.conversationId) {
      return undefined;
    }
    return conversationFolderStatusMap[conversation.lastMessage.conversationId];
  }, [conversation, conversationFolderStatusMap]);

  const [agentDropdownOpen, setAgentDropdownOpen] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState<number | null>(null);
  const [assigningAgent, setAssigningAgent] = useState(false);
  const [markingAsDone, setMarkingAsDone] = useState(false);

  const platformInfo = getPlatformInfo(selectedChannel);
  const fanpageName = selectedFanpageName || conversation?.name || "";

  const settingMenuItem = useMemo(() => {
    if (location.pathname.startsWith("/settings")) {
      const settingGroup = PLATFORM_GROUPS.find((g) => g.id === "setting");
      return settingGroup?.submenu?.find(
        (item) =>
          location.pathname === item.path ||
          location.pathname.startsWith(`${item.path}/`)
      );
    }
    return null;
  }, [location.pathname]);

  const additionalBreadcrumbItem = useMemo(() => {
    if (settingMenuItem && location.pathname.startsWith(settingMenuItem.path)) {
      const pathAfterBase = location.pathname.replace(settingMenuItem.path, "");

      if (pathAfterBase === "/create") {
        return "Tạo mẫu mới";
      }

      const idMatch = pathAfterBase.match(/^\/(\d+)$/);
      if (idMatch) {
        return "Chi tiết";
      }
    }
    return null;
  }, [settingMenuItem, location.pathname]);

  const activeAgent = useMemo(() => {
    const conversationId = conversation?.lastMessage?.conversationId;
    if (!conversationId) return null;
    return agentsByConversation[conversationId] || null;
  }, [conversation?.lastMessage?.conversationId, agentsByConversation]);

  const handleDropdownOpenChange = useCallback(
    (open: boolean) => {
      setAgentDropdownOpen(open);
      if (open) {
        setSelectedAgentId(activeAgent?.id || null);
      } else if (!open) {
        setSelectedAgentId(activeAgent?.id || null);
      }
    },
    [activeAgent]
  );

  const handleAgentSelect = useCallback((agentId: number) => {
    setSelectedAgentId(agentId);
  }, []);

  const handleAgentSave = useCallback(async () => {
    if (!conversation?.lastMessage?.conversationId || !selectedAgentId) {
      return;
    }

    if (!activePageId) {
      showError("Vui lòng chọn fanpage trước khi gán agent");
      return;
    }

    const externalId = conversation.lastMessage.conversationId;

    try {
      setAssigningAgent(true);
      await assignAgentToConversation({
        agentId: selectedAgentId,
        externalId: externalId,
        platform: PLATFORMS.FACEBOOK,
        pageId: activePageId,
      });

      if (activePageId && externalId) {
        await dispatch(
          fetchAgentsByConversations({
            externalIds: [externalId],
            platform: PLATFORMS.FACEBOOK,
            pageId: activePageId,
          })
        ).unwrap();
      }

      const triggerRefresh =
        useChatStore.getState().triggerRefreshConversationHistory;
      triggerRefresh();

      success("Gán agent thành công");
      setAgentDropdownOpen(false);
      setSelectedAgentId(selectedAgentId);
    } catch (error) {
      showError(
        error instanceof Error
          ? error.message
          : "Không thể gán agent. Vui lòng thử lại."
      );
    } finally {
      setAssigningAgent(false);
    }
  }, [conversation, selectedAgentId, activePageId]);

  const handleAgentCancel = useCallback(() => {
    setAgentDropdownOpen(false);
    setSelectedAgentId(null);
  }, []);

  const handleAgentRemove = useCallback(async () => {
    if (!conversation?.lastMessage?.conversationId || !activeAgent) {
      return;
    }

    if (!activePageId) {
      showError("Vui lòng chọn fanpage trước khi gỡ agent");
      return;
    }

    const externalId = conversation.lastMessage.conversationId;

    try {
      setAssigningAgent(true);
      await unassignAgentFromConversation({
        externalId: externalId,
        platform: PLATFORMS.FACEBOOK,
        pageId: activePageId,
      });

      dispatch(removeAgentFromConversation(externalId));

      if (activePageId && externalId) {
        await dispatch(
          fetchAgentsByConversations({
            externalIds: [externalId],
            platform: PLATFORMS.FACEBOOK,
            pageId: activePageId,
          })
        ).unwrap();
      }

      const triggerRefresh =
        useChatStore.getState().triggerRefreshConversationHistory;
      triggerRefresh();

      success("Gỡ agent thành công");
      setAgentDropdownOpen(false);
    } catch (error) {
      showError(
        error instanceof Error
          ? error.message
          : "Không thể gỡ agent. Vui lòng thử lại."
      );
    } finally {
      setAssigningAgent(false);
    }
  }, [conversation, activeAgent, activePageId, dispatch]);

  const handleMarkAsDone = useCallback(async () => {
    if (!conversation?.lastMessage?.conversationId) {
      showError("Không tìm thấy conversation");
      return;
    }
    if (!activePageId) {
      showError("Vui lòng chọn fanpage trước khi đánh dấu xong");
      return;
    }
    const conversationId = conversation.lastMessage.conversationId;
    try {
      setMarkingAsDone(true);
      const response = await markConversationAsDone({
        conversationId,
        pageId: activePageId,
      });

      if (response.code === 0 || response.code === 200) {
        success("Đánh dấu xong thành công");
        if (conversationId) {
          updateConversationFolderStatus({
            [conversationId]: "DONE",
          });
        }

        const triggerRefresh =
          useChatStore.getState().triggerRefreshConversationHistory;
        triggerRefresh();
      } else {
        showError(response.message || "Đánh dấu xong thất bại");
      }
    } catch (error) {
      showError(
        error instanceof Error
          ? error.message
          : "Không thể đánh dấu xong. Vui lòng thử lại."
      );
    } finally {
      setMarkingAsDone(false);
    }
  }, [conversation, activePageId, updateConversationFolderStatus]);

  const agentDropdownContent = useMemo(() => {
    if (loadingAgents) {
      return (
        <div className={styles.agentDropdownContent}>
          <div className={styles.agentLoading}>
            <Spin size="small" />
            <span>Đang tải...</span>
          </div>
        </div>
      );
    }

    if (agents.length === 0) {
      return (
        <div className={styles.agentDropdownContent}>
          <div className={styles.agentEmpty}>Không có agent nào</div>
        </div>
      );
    }

    return (
      <div className={styles.agentDropdownContent}>
        <div className={styles.agentList}>
          {agents.map((agent) => {
            const isSelected = selectedAgentId === agent.id;
            return (
              <div
                key={agent.id}
                className={`${styles.agentItem} ${
                  isSelected ? styles.agentItemSelected : ""
                }`}
                onClick={() => handleAgentSelect(agent.id)}
              >
                <div className={styles.radioIndicator}>
                  {isSelected && <div className={styles.radioDot} />}
                </div>
                <div className={styles.agentInfo}>
                  <div className={styles.agentName}>{agent.name}</div>
                </div>
                {isSelected && (
                  <CheckCircleOutlined className={styles.agentCheck} />
                )}
              </div>
            );
          })}
        </div>
        <div className={styles.agentActions}>
          <div className={styles.agentActionsLeft}>
            {activeAgent && (
              <Button
                size="small"
                danger
                icon={<UserDeleteOutlined />}
                onClick={handleAgentRemove}
                disabled={assigningAgent}
                loading={assigningAgent}
              >
                Gỡ agent
              </Button>
            )}
          </div>
          <div className={styles.agentActionsRight}>
            <Button size="small" onClick={handleAgentCancel}>
              Hủy
            </Button>
            <Button
              type="primary"
              size="small"
              onClick={handleAgentSave}
              disabled={
                !selectedAgentId ||
                !conversation?.lastMessage?.conversationId ||
                !activePageId ||
                assigningAgent ||
                activeAgent?.id === selectedAgentId
              }
              loading={assigningAgent}
            >
              Lưu
            </Button>
          </div>
        </div>
      </div>
    );
  }, [
    agents,
    loadingAgents,
    selectedAgentId,
    handleAgentSelect,
    handleAgentSave,
    handleAgentCancel,
    conversation?.lastMessage?.conversationId,
    activePageId,
    assigningAgent,
  ]);

  const handleLogout = useCallback(() => {
    auth.logout();
    apiClient.clearToken();
    success("Đăng xuất thành công!");
  }, [auth, success]);

  const [currentUserInfo, setCurrentUserInfo] = useState<{
    fullName?: string;
    name?: string;
    avatar?: string;
    username?: string;
    displayName?: string;
  } | null>(null);

  useEffect(() => {
    const loadUserInfo = () => {
      const userInfo = getLocalStorageJSON<{
        fullName?: string;
        name?: string;
        avatar?: string;
        username?: string;
        displayName?: string;
      }>("kido_current_user_info");

      if (userInfo) {
        setCurrentUserInfo(userInfo);
        return;
      }

      if (typeof window !== "undefined") {
        const windowWithKido = window as typeof window & {
          kido_current_user_info?: {
            fullName?: string;
            name?: string;
            avatar?: string;
            username?: string;
            displayName?: string;
          };
        };
        if (windowWithKido.kido_current_user_info) {
          setCurrentUserInfo(windowWithKido.kido_current_user_info);
          return;
        }
      }

      const authUser = auth.getCurrentUser();
      if (authUser) {
        setCurrentUserInfo({
          fullName: authUser.name,
          name: authUser.name,
          avatar: authUser.avatar,
          username: authUser.name,
          displayName: authUser.name,
        });
      }
    };

    loadUserInfo();

    // Listen for storage changes
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "kido_current_user_info") {
        loadUserInfo();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [auth]);

  const userName =
    currentUserInfo?.fullName ||
    currentUserInfo?.name ||
    currentUserInfo?.displayName ||
    currentUserInfo?.username ||
    "User";
  const userAvatar = currentUserInfo?.avatar || "";

  const menuItems: MenuProps["items"] = [
    {
      key: "logout",
      label: "Đăng xuất",
      icon: <LogoutOutlined />,
      onClick: handleLogout,
    },
  ];

  return (
    <div className={styles.chatHeader}>
      <div className={styles.identity}>
        <div>
          <div className={styles.title}>
            {settingMenuItem ? (
              <Breadcrumb
                separator="/"
                items={[
                  {
                    title: (
                      <span className={styles.breadcrumbTitle}>
                        <SettingOutlined
                          style={{ color: "var(--text-color)" }}
                        />
                        Cài đặt
                      </span>
                    ),
                  },
                  {
                    title: settingMenuItem.label,
                  },
                  ...(additionalBreadcrumbItem
                    ? [
                        {
                          title: additionalBreadcrumbItem,
                        },
                      ]
                    : []),
                ]}
              />
            ) : selectedFanpageName ? (
              <Breadcrumb
                separator="/"
                items={[
                  {
                    title: (
                      <span className={styles.breadcrumbTitle}>
                        {platformInfo.icon}
                        {platformInfo.label}
                      </span>
                    ),
                  },
                  {
                    title: fanpageName,
                  },
                ]}
              />
            ) : (
              <span>{conversation?.name}</span>
            )}
          </div>
        </div>
      </div>
      <div className={styles.actions}>
        {selectedChannel !== CHANNELS.ZALO && (
          <Dropdown
            open={agentDropdownOpen}
            onOpenChange={handleDropdownOpenChange}
            popupRender={() => agentDropdownContent}
            trigger={["click"]}
            placement="bottomRight"
            overlayClassName={styles.agentDropdown}
          >
            {activeAgent ? (
              <Button type="text" className={styles.activeAgentButton}>
                {activeAgent.avatarUrl ? (
                  <Avatar
                    src={activeAgent.avatarUrl}
                    size={18}
                    className={styles.activeAgentAvatar}
                  />
                ) : (
                  <Avatar
                    size={18}
                    className={styles.activeAgentAvatar}
                    style={{
                      background: getAvatarGradient(
                        activeAgent.name?.charAt(0) || "A"
                      ),
                      color: "#ffffff",
                      fontWeight: 600,
                      fontSize: "12px",
                    }}
                  >
                    {getAvatarLetter(activeAgent.name)}
                  </Avatar>
                )}
                <span className={styles.activeAgentName}>
                  {activeAgent.name}
                </span>
              </Button>
            ) : (
              <Button type="text" icon={<UserAddOutlined />}>
                Gán agent
              </Button>
            )}
          </Dropdown>
        )}
        {folderStatus !== "DONE" && selectedChannel !== CHANNELS.ZALO && (
          <Button
            type="text"
            icon={<CheckOutlined />}
            onClick={handleMarkAsDone}
            disabled={
              !conversation?.lastMessage?.conversationId ||
              !activePageId ||
              markingAsDone
            }
            loading={markingAsDone}
          >
            Đánh dấu xong
          </Button>
        )}
        <ThemeToggle />
        {isShowInfoUser && (
          <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
            <span
              style={{
                borderLeft: "1px solid var(--border-color)",
                paddingLeft: "12px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {userAvatar ? (
                <Avatar
                  src={userAvatar}
                  size={24}
                  className={styles.userAvatar}
                />
              ) : (
                <Avatar
                  size={24}
                  className={styles.userAvatar}
                  style={{
                    background: getAvatarGradient(userName?.charAt(0) || "U"),
                    color: "#ffffff",
                    fontWeight: 600,
                    fontSize: "12px",
                  }}
                >
                  {getAvatarLetter(userName)}
                </Avatar>
              )}
              <span className={styles.userName}>{userName}</span>
            </span>
          </Dropdown>
        )}
      </div>
    </div>
  );
};
